/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { FancyGraphics } from '@appCanvasCommon/graphics/FancyGraphics';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { Subject } from 'rxjs';
import { AbstractDrawingModule } from '@appCanvasCommon/core/AbstractDrawingModule';
import { CanvasAngularLand } from '../../../app-data/interfaces/canvas-angular-land';
import { takeUntil } from 'rxjs/operators';
import { cssVar } from '@touchplan/css-var';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
/**
 * Visual configuration settings for the timeline background.
 */
const settings = {
	timelineHeight: 38, // 38px from the Plan Printing spec
	color: cssVar.grey38,
};
/**
 * BackgroundDrawer is responsible for rendering the timeline background on the canvas.
 */
export class BackgroundDrawer extends AbstractDrawingModule {
	/**
	 * Container that holds the timeline background objects
	 * @private
	 */
	private _container: FancyContainer;
	/**
	 * Graphics object for drawing the horizontal timeline background
	 * @private
	 */

	private _graphics: FancyGraphics = new FancyGraphics();
	/**
	 * Cleans up any internal subscriptions when the drawer is destroyed
	 * @private
	 */
	private _shutdownSubject$: Subject<void> = new Subject<void>();
	/**
	 * Creates a new BackgroundDrawer instance.
	 * @param _canvasAngularLand - Interface to Angular services/dependencies
	 * @param _application - The canvas application instance
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, _application: AbstractApplication) {
		super(_application);
		this.setupLayers();
		this.setupSubscribers();
	}
	/**
	 * Update any positions without triggering a redraw
	 * @param delta {number} time since last frame
	 */
	public update(delta: number): void {
		// Intentionally left empty (required for AbstractDrawingModule)
	}
	/**
	 * Draws the timeline background.
	 */
	public draw(): void {
		this._graphics.clear();
		// Get the width of the current camera view (most likely the screen width)
		const width: number = this.parentApplication.camera.getScreen().width;
		this._graphics.beginFill(settings.color);
		// Using 0,0 as coordinates because we always want to draw relative to the container
		// then we simply position the container where we need it to be.
		this._graphics.drawRect(0, 0, width, settings.timelineHeight);
		this._graphics.endFill();
	}
	/**
	 * Cleans up resources when the drawer is destroyed.
	 */
	public destroy(opts?: IDestroyOptions): void {
		super.destroy(opts);
		this._shutdownSubject$.next();
		this._canvasAngularLand = null;
	}
	/**
	 * Sets up the layer structure for the timeline background.
	 * Creates a container and adds the line and label as children.
	 */
	private setupLayers(): void {
		this._container = this.parentApplication.layerManager.addLayer(LayerPaths.UI_TIMELINE_BACKGROUND);
		this.parentApplication.layerManager.addChildToLayer(LayerPaths.UI_TIMELINE_BACKGROUND, this._graphics);

		// Using 0 because we start drawing on the left edge of the screen, so we position the container along the left edge
		this._container.position.set(0, cssVar.headerHeight_px);

		this.destroyList.push(this._container);
	}

	/**
	 * Sets up subscription to today line updates from the service.
	 * Triggers redraws when the render queue says so
	 */
	private setupSubscribers(): void {
		this._canvasAngularLand.renderQueueWithCleanup$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe(() => this.draw());
	}
}
